import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import moment from "moment";
import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useFetch } from "services/hooks/useFetch";
import { theme, defaultAllSelection } from "services/constants";
import { Loader } from "components/Loader";
import sessionHandling from "services/utils/notificationUtils";
import { showToast } from "services/utils/Status";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { FilterProvider } from "pages/FilterContext";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ApartmentRounded from "@mui/icons-material/ApartmentRounded";
import MaleRounded from "@mui/icons-material/MaleRounded";
import FemaleRounded from "@mui/icons-material/FemaleRounded";
import FunctionsRounded from "@mui/icons-material/FunctionsRounded";
import { checkViewerPermission, subModuleNameMatches } from "services/utils/HelperFunction";
import { PlannedPresentReportHeader } from "pages/TeamZone/TeamZoneHeaders";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { getThisMonthRange } from "components/CustomComponents/dateRangeDefaults";
import { CompactHeaderSelectFilter } from "components/CustomComponents/CompactHeaderSelectFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { useExcelDownload } from "components/CustomComponents/useExcelDownload";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  paginationFooter,
  rowsPerPageOptions,
  StatCard,
  withDesignFont,
} from "services/constants/designTokens";
import {
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableBodyCell,
  approveTableHeadCell,
  ApproveTable,
} from "pages/TeamZone/Approve/approveUi";

const presentPlannedStatus = [
  { label: "All", value: "All" },
  { label: "Planned", value: "Planned" },
  { label: "Present", value: "Present" },
];

export const WorkforceReport = ({
  setShowReport,
  setReportHeaderActions,
  embeddedReportTypes,
  selectedReportType,
  onReportTypeChange,
}) => {
  const {
    domain,
    token,
    userDetails,
    topbarCompanyId,
    userScreenAccess,
    topbarCompanyName,
  } = useSelector((state) => state.tokenReducer);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [departmentName, setDepartmentName] = useState("All");
  const [statusName, setStatusName] = useState("All");
  const [departmentId, setDepartmentId] = useState(defaultAllSelection);
  const [workforceDetails, setWorkforceDetails] = useState({});
  const [workForceData, setWorkForceData] = useState([]);
  const [initialDateRange] = useState(() =>
    getThisMonthRange({ endAt: "today" }),
  );
  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);
  const [dateRangeLabel, setDateRangeLabel] = useState(initialDateRange.label);
  const { result: departmentData } = useFetch(
    `${domain}get-department/?user_id=${userDetails.id}&company_id=${topbarCompanyId}`,
  );
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const footerRef = useCallback((node) => setFooterNode(node), []);
  const { downloadExcel, downloadPdf, downloadCsv } = useExcelDownload();

  const teamZoneReports = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Reports",
  );

  // A raw === comparison here used to miss real payloads, whose
  // sub_module_name for this screen actually comes back as "Workforce
  // Status" (no "Report" suffix) rather than the registry's own label —
  // subModuleNameMatches applies the same alias table checkViewerPermission
  // below already relies on, so this ID lookup and that permission check
  // agree on what counts as a match instead of silently landing on
  // undefined and sending an invalid submodule id when saving a view.
  const subModuleId = teamZoneReports?.submodules?.find((sub) =>
    subModuleNameMatches(sub.sub_module_name, "Workforce Status Report"),
  )?.sub_module_id;

  const check_access = checkViewerPermission(
    userScreenAccess?.module_details,
    "Reports",
    "Workforce Status Report",
  );

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "workforce_status_report_saved_views",
    fullHeaders: PlannedPresentReportHeader,
    screenAccess: check_access,
    onViewChange: handleSavedViewChange,
  });

  useEffect(() => {
    const fetchUsertaskDetails = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(
          `${domain}team_zone/reports/gender-based-attendance-report/?start_date=${startDate}&end_date=${endDate}&department_id=${departmentId}&company_id=${topbarCompanyId}&status=${statusName}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          setWorkforceDetails(res || {});
        } else if (response.status === 409) {
          setWorkforceDetails({});
          sessionHandling();
        } else {
          setWorkforceDetails({});
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[WorkforceReport] operation failed:", error);
        setWorkforceDetails({});
        showToast(
          "error",
          "Couldn't load the workforce report. Please try again.",
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsertaskDetails();
  }, [
    domain,
    token,
    startDate,
    endDate,
    departmentId,
    statusName,
    userDetails.id,
    topbarCompanyId,
  ]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStartDate = (rangeStart, rangeEnd, rangeLabel) => {
    const parsedStartDate = moment(rangeStart);
    const parsedEndDate = moment(rangeEnd);

    if (!parsedStartDate.isValid() || !parsedEndDate.isValid()) {
      return;
    }

    setStartDate(parsedStartDate.format("YYYY-MM-DD"));
    setEndDate(parsedEndDate.format("YYYY-MM-DD"));
    setDateRangeLabel(rangeLabel || "Custom Range");
    setPage(0);
  };

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setWorkForceData(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const departmentList = useMemo(
    () => [
      {
        id: defaultAllSelection,
        label: "All",
        department_name: "All",
      },
      ...(departmentData?.map((obj) => ({
        ...obj,
        label: obj.department_name,
      })) ?? []),
    ],
    [departmentData],
  );

  const updateDepartmentName = (event, data) => {
    if (data && data?.label) {
      setDepartmentName(data.label);
      setDepartmentId(data.id);
      setPage(0);
    }
  };

  const updateStatus = (event, data) => {
    if (data && data?.label) {
      setStatusName(data.label);
      setPage(0);
    }
  };

  const getReportMetadata = useCallback(() => {
    const formattedStartDate = moment(
      startDate,
      ["DD/MM/YYYY", "YYYY-MM-DD"],
      true,
    ).format("DD/MM/YYYY");
    const formattedEndDate = moment(
      endDate,
      ["DD/MM/YYYY", "YYYY-MM-DD"],
      true,
    ).format("DD/MM/YYYY");

    return {
      "Company Name": topbarCompanyName || "N/A",
      Department: departmentName || "All",
      Status: statusName || "All",
      "Date Range": `${formattedStartDate} to ${formattedEndDate}`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
      Departments: workForceData?.length || 0,
      "Overall Male Count": workforceDetails?.over_all_male_user_count || 0,
      "Overall Female Count": workforceDetails?.over_all_female_user_count || 0,
      "Overall Total Count": workforceDetails?.over_all_total || 0,
    };
  }, [
    departmentName,
    endDate,
    startDate,
    statusName,
    topbarCompanyName,
    workForceData?.length,
    workforceDetails,
  ]);

  const handleWorkforceDownload = useCallback(
    async (exportRows, onProgress, downloadFormat = "excel") => {
      const rows = Array.isArray(exportRows) ? exportRows : [];
      const summaryRow = {
        rowType: "grand_total",
        department_name: "TOTAL",
        over_all_male_user_count: workforceDetails?.over_all_male_user_count || 0,
        over_all_female_user_count:
          workforceDetails?.over_all_female_user_count || 0,
        over_all_total: workforceDetails?.over_all_total || 0,
      };

      const exportToFormat =
        downloadFormat === "pdf"
          ? downloadPdf
          : downloadFormat === "csv"
            ? downloadCsv
            : downloadExcel;

      await exportToFormat(
        [...rows, summaryRow],
        visibleHeaders,
        "Workforce Status Report",
        {},
        getReportMetadata(),
        [],
      );

      if (typeof onProgress === "function") {
        onProgress(100);
      }
    },
    [
      downloadCsv,
      downloadExcel,
      downloadPdf,
      getReportMetadata,
      visibleHeaders,
      workforceDetails,
    ],
  );

  // The response's own overall_* totals (already computed server-side for
  // the current date/department/status filter) drive the Male/Female/Total
  // cards — the same numbers the table's TOTAL footer row shows further
  // down. Departments reflects the currently visible (searched) rows,
  // matching how every other report's first card counts filtered rows.
  const workforceSummaryCards = useMemo(
    () => [
      {
        key: "departments",
        title: "Departments",
        value: workForceData?.length || 0,
        icon: ApartmentRounded,
        accent: "#1D4ED8",
        tint: "#EEF4FF",
      },
      {
        key: "male",
        title: "Male",
        value: workforceDetails?.over_all_male_user_count || 0,
        icon: MaleRounded,
        accent: "#0F766E",
        tint: "#E6F8F4",
      },
      {
        key: "female",
        title: "Female",
        value: workforceDetails?.over_all_female_user_count || 0,
        icon: FemaleRounded,
        accent: "#7C3AED",
        tint: "#F5F3FF",
      },
      {
        key: "total",
        title: "Total",
        value: workforceDetails?.over_all_total || 0,
        icon: FunctionsRounded,
        accent: "#F97316",
        tint: "#FFF7ED",
      },
    ],
    [workForceData?.length, workforceDetails],
  );

  const selectedReportTypeOption =
    embeddedReportTypes?.find(
      (option) =>
        option?.label === selectedReportType ||
        option?.value === selectedReportType,
    ) || null;

  const headerFilterControls = useMemo(
    () => (
      <Mui.Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        flexWrap="wrap"
        justifyContent="flex-start"
        sx={{
          width: "100%",
          alignItems: "center",
        }}
      >
        <ApprovalDateRangeFilter
          thisMonthToDate
          startDate={startDate}
          endDate={endDate}
          dateRangeLabel={dateRangeLabel}
          onChange={handleStartDate}
          showToday={true}
          includeCurrentDate={true}
          sx={{
            minHeight: 40,
            height: 40,
            px: 2,
            width: { xs: "100%", sm: "auto" },
            backgroundColor: designColors.slate100,
            border: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: designColors.slate100,
              border: "none",
              boxShadow: "none",
            },
          }}
        />
        <CompactHeaderSelectFilter
          dataList={departmentList}
          selectedValue={departmentName}
          updateDataFunction={updateDepartmentName}
          Icons={PersonOutlineIcon}
          staticLabel="Department"
          widthSx={{
            width: { xs: "100%", sm: "auto" },
            minWidth: { xs: "100%", sm: 220 },
          }}
        />
        <CompactHeaderSelectFilter
          dataList={presentPlannedStatus}
          selectedValue={statusName}
          updateDataFunction={updateStatus}
          Icons={PersonOutlineIcon}
          staticLabel="Status"
          widthSx={{
            width: { xs: "100%", sm: "auto" },
            minWidth: { xs: "100%", sm: 200 },
          }}
        />
        {embeddedReportTypes?.length ? (
          <CompactHeaderSelectFilter
            dataList={embeddedReportTypes}
            selectedValue={selectedReportTypeOption}
            updateDataFunction={onReportTypeChange}
            Icons={PersonOutlineIcon}
            staticLabel="Report Type"
            widthSx={{
              width: { xs: "100%", sm: "auto" },
              minWidth: { xs: "100%", sm: 260 },
            }}
          />
        ) : null}
      </Mui.Stack>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      startDate,
      endDate,
      dateRangeLabel,
      departmentList,
      departmentName,
      statusName,
      embeddedReportTypes,
      selectedReportTypeOption,
      onReportTypeChange,
    ],
  );

  // Filters render inline in the page body (matching Employee Attendance's
  // own row below the title), not floated beside the title.
  useEffect(() => {
    if (!setReportHeaderActions) return undefined;
    setReportHeaderActions(null);
    return () => setReportHeaderActions(null);
  }, [setReportHeaderActions]);

  useEffect(() => {
    if (!customFilterNode) return undefined;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode, visibleHeaders.length]);

  useEffect(() => {
    if (!footerNode) return undefined;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  // Keyed by column so the TOTAL row's values stay under the right column
  // no matter how the Save View picker reorders/hides them.
  const totalRowValueByKey = {
    department_name: "TOTAL",
    over_all_male_user_count: workforceDetails?.over_all_male_user_count,
    over_all_female_user_count: workforceDetails?.over_all_female_user_count,
    over_all_total: workforceDetails?.over_all_total,
  };

  return (
    <ThemeProvider theme={withDesignFont(theme)}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        <Box sx={{ pb: 0, width: "100%", maxWidth: "100%" }}>
          {/* Filters sit in their own row directly under the page title —
              same placement as Employee Attendance Report — instead of
              floating beside the title via setReportHeaderActions. */}
          <Mui.Box sx={{ ...designShell.sectionCard, p: 1.5, mb: 2 }}>
            {headerFilterControls}
          </Mui.Box>
          <Mui.Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
                xl: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1.5,
              mb: 2,
            }}
          >
            {workforceSummaryCards.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                title={card.title}
                value={card.value}
                accent={card.accent}
                tint={card.tint}
              />
            ))}
          </Mui.Box>

          <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
            <CustomFilter
              title="Workforce Status"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={workforceDetails?.department_data || []}
              filteredData={workForceData}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={check_access}
              showtitle_count={true}
              hideCount={true}
              isAttendanceScreenReport={true}
              showDownloadOptions={false}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={getReportMetadata()}
              customDownloadHandler={handleWorkforceDownload}
              labeledToolbar={true}
              searchPlaceholder="Search by department..."
            />
          </Box>

          <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
            {isLoading ? (
              <Loader info="Loading..." />
            ) : (
              <Mui.Box>
                <Mui.TableContainer
                  sx={{
                    boxSizing: "border-box",
                    width: "100%",
                    maxWidth: "100%",
                    overflowX: { xs: "auto", sm: "visible" },
                    overflowY: { xs: "auto", sm: "visible" },
                    pb: footerHeight ? `${footerHeight}px` : 0,
                    borderLeft: `1px solid ${designColors.slate200}`,
                    borderRight: `1px solid ${designColors.slate200}`,
                    borderBottom: `1px solid ${designColors.slate200}`,
                  }}
                >
                  <ApproveTable stickyHeader sx={{ minWidth: 900, width: "100%" }}>
                    <Mui.TableHead sx={designShell.tableStickyHead}>
                      <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                        {visibleHeaders.map((header, headerIndex) => (
                          <Mui.TableCell
                            key={header.key}
                            sx={{
                              ...approveTableHeadCell,
                              top: APP_BAR_OFFSET + customFilterHeight,
                              ...(headerIndex === 0
                                ? { borderTopLeftRadius: 0 }
                                : {}),
                              ...(headerIndex === visibleHeaders.length - 1
                                ? { borderTopRightRadius: 0 }
                                : {}),
                            }}
                          >
                            {header.name}
                          </Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                    </Mui.TableHead>
                    <Mui.TableBody>
                      {workforceDetails?.department_data?.length === 0 ? (
                        <TableEmptyState
                          dataLength={workforceDetails?.department_data}
                        />
                      ) : (
                        workForceData
                          ?.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                          .map((department, index) => (
                            <Mui.TableRow
                              key={`${department.department_name}-${index}`}
                              hover
                              sx={{
                                ...designShell.tableRowHover,
                                "& td": { backgroundColor: designColors.white },
                              }}
                            >
                              {visibleHeaders.map((header) => (
                                <Mui.TableCell
                                  key={`${department.department_name}-${header.key}`}
                                  sx={approveTableBodyCell}
                                >
                                  {department[header.key]
                                    ? department[header.key]
                                    : "0"}
                                </Mui.TableCell>
                              ))}
                            </Mui.TableRow>
                          ))
                      )}
                    </Mui.TableBody>
                    {workForceData?.length > 0 ? (
                      <Mui.TableFooter>
                        <Mui.TableRow>
                          {visibleHeaders.map((header) => (
                            <Mui.TableCell
                              key={header.key}
                              sx={{
                                ...approveTableBodyCell,
                                fontWeight: 700,
                                color: designColors.slate900,
                                backgroundColor: designColors.slate100,
                                borderBottom: "none",
                              }}
                            >
                              {totalRowValueByKey[header.key] ?? ""}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>
                      </Mui.TableFooter>
                    ) : null}
                  </ApproveTable>
                </Mui.TableContainer>
              </Mui.Box>
            )}

            <Mui.Box ref={footerRef} sx={approveStickyFooterBar}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: workForceData?.length || 0,
                itemLabel: "departments",
                onPageChange: setPage,
                onRowsPerPageChange: handleChangeRowsPerPage,
                rowsPerPageOptions,
              })}
            </Mui.Box>
          </Box>
        </Box>
      </FilterProvider>
    </ThemeProvider>
  );
};
